// frontend/src/components/MainGrid.js
import * as React from 'react';
import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Copyright from '../internals/components/Copyright';
import HighlightedCard from './HighlightedCard';
import MapCard from './MapCard';
import StatCard from './StatCard';
import CardAlert from './CardAlert';
import LLMInsightCard from './LLMInsightCard'; // Import the new component
import { authFetch } from '../authFetch';

// Initial state for the cards to prevent errors on first render
const initialData = [
  {
    title: 'Infrastructures at high risk',
    value: '...',
    interval: 'Loading...',
    trend: 'neutral',
    data: Array(30).fill(null),
  },
  {
    title: 'Population potentially affected',
    value: '...',
    interval: 'Loading...',
    trend: 'neutral',
    data: Array(30).fill(null),
  },
];

export default function MainGrid() {
  const [statsData, setStatsData] = useState(initialData);
  // State for LLM data and loading
  const [llmInsight, setLlmInsight] = useState('');
  const [loadingLlm, setLoadingLlm] = useState(true);
  // **NEW**: State for the overall risk level
  const [riskConditions, setRiskConditions] = useState('low'); // Default to 'low'

  // Fetch all data when the component mounts
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await authFetch('/api/daily-stats/');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setStatsData([data.vulnerabilities, data.population]);
      } catch (error) {
        console.error("Error fetching daily stats:", error);
      }
    };

    const fetchLlmSummary = async () => {
      setLoadingLlm(true);
      try {
        const response = await authFetch('/api/llm-summary/');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setLlmInsight(data.overview);
      } catch (error) {
        console.error("Error fetching LLM summary:", error);
        setLlmInsight('Could not load the AI-powered summary due to an error.');
      } finally {
        setLoadingLlm(false);
      }
    };

    // **NEW**: Fetch flood data to determine the overall risk level
    const fetchRiskLevel = async () => {
      // **MODIFIED**: Point to the new, more accurate API endpoint
      try {
        const response = await authFetch('/api/risk-overview/');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const apiResponse = await response.json();
        
        // **MODIFIED**: Directly use the calculated risk level from the backend
        setRiskConditions(apiResponse.risk_level || 'low');
      } catch (error) {
        console.error("Error fetching flood data for risk level:", error);
        // Keep default 'low' on error or set to a specific error state
      }
    };


    fetchStats();
    fetchLlmSummary();
    fetchRiskLevel(); // **NEW**: Call the new function
  }, []);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Overview
      </Typography>
      <Grid
        container
        spacing={2}
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2) }}
      >
        {/* --- TOP ROW --- */}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          {/* **MODIFIED**: Pass the dynamic risk state to the component */}
          <CardAlert conditions={riskConditions} />
        </Grid>
        {statsData.map((card, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard {...card} />
          </Grid>
        ))}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <HighlightedCard />
        </Grid>

        {/* --- LLM INSIGHT ROW --- */}
        <Grid size={{ xs: 12 }}>
          <LLMInsightCard insight={llmInsight} loading={loadingLlm} />
        </Grid>

        {/* --- MAP ROW --- */}
        <Grid size={{ xs: 12 }}>
          <MapCard />
        </Grid>
      </Grid>
      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}